/**
 * Neutral comparison content for families evaluating education platforms.
 *
 * Kept in a data-only module (no JSX) so route `head()` can server-render the
 * page metadata and schema without pulling the page component in.
 */
const BASE_URL = "https://leadxkids.com";

export interface ComparisonRow {
  feature: string;
  leadxkids: string;
  competitor: string;
}

export interface Comparison {
  slug: string;
  competitorName: string;
  /** Optional metadata-only title; visible comparison copy remains unchanged. */
  seoTitle?: string;
  intent: string;
  summary: string;
  whoIsItFor: string;
  whoIsLeadxkidsFor: string;
  rows: ComparisonRow[];
  honestNote: string;
}

export const comparisons: Record<string, Comparison> = {
  "other-edtech-firms": {
    slug: "other-edtech-firms",
    competitorName: "other edtech firms",
    seoTitle: "LeadXkids and Other Edtech Firms Compared",
    intent: "children's learning",
    summary:
      "Other edtech firms may focus on academic tutoring or career guidance; LeadXkids offers story-based, self-paced life-skills practice for Indian children aged 8–14.",
    whoIsItFor:
      "Families seeking academic tutoring, exam preparation or career guidance may prefer a provider specialising in those needs.",
    whoIsLeadxkidsFor:
      "Parents of children aged 8–14 who want story-based practice in confidence, communication and everyday life skills.",
    honestNote:
      "If your primary need is academic catch-up, exam preparation or career counselling, a specialist provider may be a better fit. LeadXkids focuses on life skills rather than coursework or career mapping.",
    rows: [
      { feature: "Primary focus", leadxkids: "Confidence, communication, life skills", competitor: "Varies by provider" },
      { feature: "Age band", leadxkids: "8–14", competitor: "Varies by provider" },
      { feature: "Format", leadxkids: "Self-paced, story-based practice", competitor: "May include classes, tutoring, assessments or counselling" },
      { feature: "Learning approach", leadxkids: "Scenarios, choices, reflection and practical application", competitor: "Varies by provider" },
      { feature: "Academic tutoring", leadxkids: "Not offered", competitor: "Often available" },
      { feature: "Career counselling", leadxkids: "Not offered", competitor: "Available from specialist providers" },
    ],
  },
};

/** Article + FAQ + Breadcrumb schema for a comparison page. */
export function comparisonSchemas(data: Comparison): Record<string, unknown>[] {
  const pageUrl = `${BASE_URL}/vs/${data.slug}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: `LeadXkids vs ${data.competitorName} for ${data.intent}`,
      description: data.summary,
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
      author: { "@type": "Organization", name: "LeadXkids", "@id": `${BASE_URL}/#organization` },
      publisher: {
        "@type": "Organization",
        name: "LeadXkids",
        "@id": `${BASE_URL}/#organization`,
        logo: { "@type": "ImageObject", url: `${BASE_URL}/lx-logo-new.png` },
      },
      about: [
        { "@type": "Thing", name: "LeadXkids" },
        { "@type": "Thing", name: data.competitorName },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `Is LeadXkids better than ${data.competitorName}?`,
          acceptedAnswer: { "@type": "Answer", text: data.summary },
        },
        {
          "@type": "Question",
          name: `Who is ${data.competitorName} best for?`,
          acceptedAnswer: { "@type": "Answer", text: data.whoIsItFor },
        },
        {
          "@type": "Question",
          name: "Who is LeadXkids best for?",
          acceptedAnswer: { "@type": "Answer", text: data.whoIsLeadxkidsFor },
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Compare", item: pageUrl },
      ],
    },
  ];
}
